// Line-ups (24 Oct 2026, Year 1): a row of 3 to 6 children, each with a name
// under them, or of animals, each with its word under it, and a "front"
// arrow at one end. Everyone faces the arrow, so first, next, last, between,
// in front of and behind can be read off the picture. Nothing in the row
// prints a place number, and which place anyone is in is never written or
// spoken: working it out is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const LINE_UP_ANIMALS = [
  'dog',
  'cat',
  'duck',
  'pig',
  'goat',
  'sheep',
  'hen',
  'rabbit',
] as const;

export type LineUpAnimal = (typeof LINE_UP_ANIMALS)[number];

export type LineUpSpec = {
  type: 'line_up';
  kind: 'children' | 'animals';
  // The end the arrow is at. Everyone faces that way.
  front: 'left' | 'right';
  // Who is in the line, from the front to the back: the first one stands
  // next to the arrow. Children by name ("Mia"), animals by word ("dog").
  members: string[];
};

const FEWEST = 3;
const MOST = 6;
// A first name of 2 to 8 letters, with a capital first: "Mia", "Kai".
const NAME = /^[A-Z][a-z]{1,7}$/;

function isAnimal(value: unknown): value is LineUpAnimal {
  return (LINE_UP_ANIMALS as readonly unknown[]).includes(value);
}

export function readLineUp(record: Record<string, unknown>): LineUpSpec | null {
  const { kind, members } = record;
  const front = record.front ?? 'left';
  if (kind !== 'children' && kind !== 'animals') return null;
  if (front !== 'left' && front !== 'right') return null;
  if (!Array.isArray(members)) return null;
  if (members.length < FEWEST || members.length > MOST) return null;
  const fits =
    kind === 'animals'
      ? members.every(isAnimal)
      : members.every(
          (member) => typeof member === 'string' && NAME.test(member),
        );
  if (!fits) return null;
  const named = members as string[];
  // Each one once, so "behind the cat" names one cat.
  if (new Set(named).size !== named.length) return null;
  return { type: 'line_up', kind, front, members: [...named] };
}

function list(words: string[]): string {
  return words.length < 2
    ? (words[0] ?? '')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

// Left to right as drawn: the front is on the left, or the line is turned
// round.
export function membersLeftToRight(spec: LineUpSpec): string[] {
  return spec.front === 'left'
    ? [...spec.members]
    : [...spec.members].reverse();
}

// What a screen reader is told in place of the picture: who is in the line,
// left to right as a sighted child reads it, and which end is the front.
// Never anyone's place ("first", "third").
export function describeLineUp(spec: LineUpSpec): string {
  const who = spec.kind === 'children' ? 'children' : 'animals';
  return `A line of ${String(spec.members.length)} ${who}. The front of the line is on the ${spec.front}. From left to right: ${list(membersLeftToRight(spec))}.`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type LineUpPicture = {
  // Left to right; x is the middle of each one.
  members: { name: string; x: number }[];
  // The middle of each figure, top to bottom.
  figureY: number;
  // Where each name is written.
  nameY: number;
  // The width and height of one figure's square.
  size: number;
  // The arrow runs from `from` to `to` (its point) along `y`; "front" is
  // written above its middle.
  arrow: { from: number; to: number; y: number; wordY: number };
  width: number;
  height: number;
};

export const LINE_UP_SIZE = 46;
const ARROW_ROOM = 44;
const EDGE = 8;

export function lineUpPicture(spec: LineUpSpec): LineUpPicture {
  const size = LINE_UP_SIZE;
  const count = spec.members.length;
  const figureY = EDGE + size / 2;
  const nameY = EDGE + size + 14;
  const rowWidth = count * size;
  // The arrow sits beyond the front one, pointing away from the line.
  const rowStart = spec.front === 'left' ? EDGE + ARROW_ROOM : EDGE;
  const members = membersLeftToRight(spec).map((name, index) => ({
    name,
    x: rowStart + index * size + size / 2,
  }));
  const arrowY = figureY + 4;
  const arrow =
    spec.front === 'left'
      ? {
          from: EDGE + ARROW_ROOM - 6,
          to: EDGE + 2,
          y: arrowY,
          wordY: arrowY - 10,
        }
      : {
          from: EDGE + rowWidth + 6,
          to: EDGE + rowWidth + ARROW_ROOM - 2,
          y: arrowY,
          wordY: arrowY - 10,
        };
  return {
    members,
    figureY,
    nameY,
    size,
    arrow,
    width: EDGE * 2 + ARROW_ROOM + rowWidth,
    height: nameY + 6 + EDGE,
  };
}
